import React, { useState, useEffect } from "react";
import axios from "axios";
import Modal from "../../../../hooks/Modal";
import { getAllAssociations } from "../../../../services/associationsServices";

const LightSummaryCards = () => {
  const [clubData, setClubData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchAllClubsData();
  }, []);

  const fetchAllClubsData = async () => {
    setLoading(true);

    try {
      const allAssociations = await getAllAssociations();

      const filtered = allAssociations.filter(
        (asso) =>
          asso.produit_asso === "MonAppliClub Light" &&
          !!asso.apiUrl_asso &&
          asso.apiUrl_asso.startsWith("http")
      );

      const results = await Promise.all(
        filtered.map((asso) =>
          fetchClubDetails({
            id: asso.id,
            name: asso.nom_asso,
            logo: asso.logo_asso,
            appUrl: asso.siteweb_asso,
            apiUrl: asso.apiUrl_asso,
            dataPaths: {
              visitsTotal: "/api/visites/countVisites",
              adherentsCount: "/api/adherents/countAdherents",
              notifications: "/api/notifications/1",
            },
          })
        )
      );

      setClubData(results);
    } catch (e) {
      console.error("Erreur lors du chargement des clubs :", e?.message);
      setClubData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchClubDetails = async (club) => {
    const clubDetails = {
      name: club.name,
      logo: club.logo,
      appUrl: club.appUrl,
      visits: 0,
      adherents: 0,
      notifications: [],
    };

    // Lancer les 3 requêtes en parallèle pour ce club
    const entries = Object.entries(club.dataPaths);
    const requests = entries.map(([key, path]) =>
      axios
        .get(`${club.apiUrl}${path}`)
        .then((res) => ({ key, data: res.data }))
        .catch((err) => {
          console.error(`Erreur ${club.name} -> ${key}:`, err?.message);
          return { key, data: null, error: true };
        })
    );

    const responses = await Promise.all(requests);

    for (const { key, data } of responses) {
      if (!data) continue;
      switch (key) {
        case "visitsTotal":
          // { count: number }
          clubDetails.visits = typeof data.count === "number" ? data.count : 0;
          break;
        case "adherentsCount":
          // { count: number }
          clubDetails.adherents =
            typeof data.count === "number" ? data.count : 0;
          break;
        case "notifications":
          // Array<{ created_at: string, ... }>
          clubDetails.notifications = Array.isArray(data)
            ? [...data].sort(
                (a, b) =>
                  new Date(b.created_at).getTime() -
                  new Date(a.created_at).getTime()
              )
            : [];
          break;
        default:
          break;
      }
    }

    return clubDetails;
  };

  const formatDateTime = (dateTime) => {
    const options = {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Intl.DateTimeFormat("fr-FR", options).format(new Date(dateTime));
  };

  const handleViewNotifications = (notifications) => {
    setSelectedNotifications(notifications || []);
    setIsModalOpen(true);
  };

  const totalStats = clubData.reduce(
    (acc, club) => {
      acc.visits += club.visits || 0;
      acc.adherents += club.adherents || 0;
      acc.notifications += club.notifications?.length || 0;
      return acc;
    },
    { visits: 0, adherents: 0, notifications: 0 }
  );

  return (
    <div>
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold mb-4">
          Statistiques des Clubs — MonAppliClub Light
        </h2>
        <button
          onClick={fetchAllClubsData}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
        >
          Rafraîchir
        </button>
      </div>

      {loading ? (
        <p>Chargement des données...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg shadow-lg">
          <table className="min-w-full bg-white border border-gray-200 rounded-lg shadow-lg">
            <thead>
              <tr className="bg-gray-100 text-left">
                <th className="py-3 px-6">Club</th>
                <th className="py-3 px-6">Visites totales</th>
                <th className="py-3 px-6">Adhérents</th>
                <th className="py-3 px-6">Notifications</th>
              </tr>
            </thead>
            <tbody>
              {clubData.map((club, index) => (
                <tr
                  key={club.name + index}
                  className={`border-b ${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  }`}
                >
                  <td className="py-3 px-6 flex items-center space-x-4">
                    <img
                      src={club.logo}
                      alt={club.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <a
                      href={club.appUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      {club.name}
                    </a>
                  </td>

                  <td className="py-3 px-6">
                    {typeof club.visits === "number" ? club.visits : 0}
                  </td>

                  <td className="py-3 px-6">
                    {typeof club.adherents === "number" ? club.adherents : 0}
                  </td>

                  <td className="py-3 px-6">
                    <button
                      className={`px-3 py-1 rounded text-white text-sm ${
                        club.notifications?.length > 0
                          ? "bg-blue-500 hover:bg-blue-600"
                          : "bg-gray-400"
                      }`}
                      disabled={!club.notifications || club.notifications.length === 0}
                      onClick={() => handleViewNotifications(club.notifications)}
                    >
                      {club.notifications?.length || 0}
                    </button>
                  </td>
                </tr>
              ))}

              <tr className="bg-gray-200 font-bold">
                <td className="py-3 px-6">Total</td>
                <td className="py-3 px-6">{totalStats.visits}</td>
                <td className="py-3 px-6">{totalStats.adherents}</td>
                <td className="py-3 px-6">{totalStats.notifications}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {isModalOpen && (
        <Modal onClose={() => setIsModalOpen(false)} title="Notifications">
          <table className="min-w-full bg-gray-200 text-gray-800">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-400">Message</th>
                <th className="py-2 px-4 border-b border-gray-400">Date</th>
              </tr>
            </thead>
            <tbody>
              {selectedNotifications.map((notification, index) => (
                <tr key={notification.id ?? index}>
                  <td className="py-2 px-4 border-b border-gray-400">
                    {notification.message}
                  </td>
                  <td className="py-2 px-4 border-b border-gray-400">
                    {formatDateTime(notification.created_at)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Modal>
      )}
    </div>
  );
};

export default LightSummaryCards;
